<script setup>

import UserPassword from "./user-password.vue";
import UserPermissions from "./user-permissions.vue";
import VueMultiselect from "vue-multiselect";
import {ref, reactive, computed, set} from "vue"
import helpers from "@/lib/helpers";
import store from "@/store";

const props = defineProps({
  url: String
})

const user = ref(null)
const mode = ref('general')
const edit = ref({})
const modalref = ref()
const available = ref({
  roles: {},
  all: []
})


const modes = computed(() => {
  const m = [{text: 'General', value: 'general'}, {text: 'Permissions', value: 'permission'}]
  if (!edit.value.service)
    m.unshift({text: 'Password', value: 'password'})

  return m
})

const refresh = async () => {
  if (props.url) {
    try {
      user.value = await helpers.asyncAction(props.url, 'GET');
    } catch {
      mode.value = 'new'
    }
  } else
    mode.value = 'new';

  initEdit();
}

const initEdit = () => {
  edit.value = {};
  ['name', 'userid', 'role', 'permissions', 'disabled', 'fkvendor', 'fkwarehouse', 'pay_schedule', 'allowed_warehouses', 'sms_phone', 'service', 'message_from_email', 'work_email'].forEach(p => {
    set(edit.value, p, user.value ? user.value[p] : null)
  })
}

const impersonate = async () => {
  if (await helpers.confirm('Impersonate user account "' + user.value.userid + '"')) {
    await store.dispatch('impersonate', user.value._links.impersonate_url.href)
    modalref.value.close()
    await helpers.alert('Now using account "' + user.value.userid + '"', true)
    await store.dispatch('settings_load')
  }
}

// too many wrong passwords lock sign-in for a while; an admin can lift it now
const lockedUntil = computed(() => user.value?.login_locked_until ? new Date(user.value.login_locked_until).toLocaleString([], {dateStyle: 'short', timeStyle: 'short'}) : null)

const unlock = async () => {
  user.value = await helpers.asyncAction(user.value._links.self_url.href, 'PUT', {data: {unlock: true}})
  helpers.toast("Sign-in unlocked")
}

const save = async () => {
  edit.value.disabled = !!edit.value.disabled

  if (user.value) {
    user.value = await helpers.asyncAction(user.value._links.self_url.href, 'PUT', {data: edit.value})
    helpers.toast("User updated")
  } else {
    user.value = await helpers.asyncAction('/api/user', 'POST', {data: edit.value})
    helpers.toast("User created")
    mode.value = 'general'
  }

  initEdit()
}

refresh()
helpers.asyncAction('/api/settings/permissions', 'get').then(v => available.value = v)
</script>

<template>
  <modal height="750px" use-max-height large :title="mode === 'new' ? 'Create New User' : 'User'" ref="modalref">
    <template #top>
      <template v-if="!!user">
        <div v-if="user" class="">
          <div class="d-flex flex-fill">
            <div class="h5 flex-fill">{{ user.id }} - {{ user.userid }}</div>

            <b-badge variant="danger" class="py-2 px-4 my-auto" v-if="user.disabled">DISABLED</b-badge>
            <b-button v-else-if="can('users.impersonate')" variant="link" size="sm" @click="impersonate">
              Impersonate
            </b-button>
          </div>
          <div>{{ user.name }}</div>
          <div v-if="lockedUntil" class="d-flex align-items-center text-danger small">
            Sign-in locked until {{ lockedUntil }} after too many wrong passwords
            <b-button v-if="can('users.write')" variant="link" size="sm" class="py-0" @click="unlock">Unlock</b-button>
          </div>
        </div>

        <mode-selector breakpoint="md" class="mt-3"
                       v-model="mode"
                       :modes="modes"/>
      </template>
    </template>

    <div class="p-3">
      <user-password v-if="mode === 'password'" :user="user"/>
      <template v-else-if="mode === 'general' || mode === 'new'">

        <form-row label="Service Account">
          <div class="my-1">
            <b-check switch :checked="!!edit.service" @input="(v) => edit.service = v"/>
          </div>
          <div class="font-italic">Service account can only authorize with JWT tokens.</div>
        </form-row>

        <form-divider/>

        <modal-row label="User ID">
          <b-form-input size="sm" v-model="edit.userid"/>
        </modal-row>
        <modal-row label="Name">
          <b-form-input size="sm" v-model="edit.name"/>
        </modal-row>
        <modal-row label="SMS Phone">
          <b-form-input size="sm" v-model="edit.sms_phone"/>
        </modal-row>
        <modal-row label="Work Email">
          <b-form-input size="sm" type="email" v-model="edit.work_email" placeholder="name@ntc-tech.com"/>
          <b-form-text>
            This person's company mailbox. Email they send from it to the assistant address lands in their assistant inbox.
          </b-form-text>
        </modal-row>
        <modal-row label="Message From">
          <b-form-input size="sm" type="email" v-model="edit.message_from_email"
                        placeholder="Leave blank to use the system default"/>
          <b-form-text>
            Outbound messages and replies from this user are sent as this address.
            It must already exist on the mail server, or the send is rejected.
          </b-form-text>
        </modal-row>
        <modal-row label="Disabled" v-if="mode !== 'new'">
          <div class="my-1">
            <b-check switch :checked="!!edit.disabled" @input="(v) => edit.disabled = v"/>
          </div>
        </modal-row>
        <form-divider/>

        <form-row label="Allowed Warehouses">
          <vue-multiselect placeholder="Allow All" track-by="value" multiple label="name"
                           :value="edit.allowed_warehouses ? edit.allowed_warehouses.map(f => $store.state.settings.defaults.warehouses.find(g => g.value === f)) : []"
                           @input="(v) => edit.allowed_warehouses = v.map(g => g.value)"
                           :options="$store.state.settings.defaults.warehouses"/>
        </form-row>

        <form-row label="Warehouse">
          <b-select :options="$store.state.settings.defaults.warehouses"
                    v-model="edit.fkwarehouse"/>
        </form-row>

        <form-divider/>

        <modal-row label="Pay Schedule">
          <b-select :options="[{ text: '---- None Selected ----', value: null}, ...store.state.settings.defaults.pay_schedules.map(f => f.name)]" v-model="edit.pay_schedule"/>
        </modal-row>

        <form-divider/>

        <form-row label="Vendor Scope" v-if="can('vendors.index')">
          <form-select-remote-search v-model="edit.fkvendor" key-text="description" key-value="id"
                                     url="/api/vendor?limit=100"
                                     search-url="/api/vendor?q="/>
        </form-row>


        <template v-if="mode === 'new'">
          <modal-row label="Role">
            <div class="my-2">
              <b-select :options="Object.keys(available.roles)" v-model="edit.role" size="sm"/>
            </div>
          </modal-row>

          <form-divider/>

          <modal-row label="Password" v-if="!edit.service">
            <form class="my-3">
              <b-form-input autocomplete="new-password" type="password" v-model="edit.password" ref="password"
                            placeholder="Password"/>
            </form>
          </modal-row>

          <modal-row label="Pin" v-if="!edit.service">
            <form class="my-3">
              <b-form-input autocomplete="new-password" v-model="edit.pin" type="password" ref="pin"
                            placeholder="Pin"/>
            </form>
          </modal-row>
        </template>

      </template>
      <user-permissions :available="available" v-else-if="mode === 'permission'" v-model="edit"/>
    </div>

    <template #footer v-if="can('users.write')">
      <b-button variant="primary" size="sm" class="px-4" @click="save" :disabled="mode === 'password'">Save</b-button>
    </template>
  </modal>
</template>

<style scoped>

</style>